import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Widget types map 1:1 to a renderer in DashboardWidget.tsx
export type WidgetType =
  | 'number'          // Big number — e.g. "Total items", "Overdue", "Done this week"
  | 'battery'         // Horizontal % bar — e.g. "% done on Marketing"
  | 'status_chart'    // Pie/donut of items by status
  | 'priority_chart'  // Bar of items by priority
  | 'workload'        // Per-person stacked bar (done / working / stuck / not_started)
  | 'trend'           // Line chart of items created or done over time
  | 'board_list';     // Compact list of items from selected board

export type MetricKind =
  | 'total_items'
  | 'done'
  | 'overdue'
  | 'working'
  | 'due_this_week';

export interface Widget {
  id: string;
  type: WidgetType;
  title: string;
  boardIds: string[];       // boards to aggregate. empty = all
  settings: Record<string, unknown>; // type-specific: metric, color, period
  x: number; y: number;     // grid position (12-col)
  w: number; h: number;     // size (cols x rows, row ≈ 120px)
}

export interface Dashboard {
  id: string;
  name: string;
  icon: string;
  color: string;  // gradient class tail
  description?: string;
  widgets: Widget[];
  createdAt: string;
  updatedAt: string;
}

const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const now = () => new Date().toISOString();

function makeSeed(): Dashboard[] {
  const mkW = (p: Partial<Widget> & Pick<Widget, 'type' | 'title' | 'x' | 'y' | 'w' | 'h'>): Widget => ({
    id: uid('wg'), boardIds: [], settings: {}, ...p,
  });

  return [
    {
      id: 'dash_overview',
      name: 'Team Overview',
      icon: '',
      color: 'from-neon-blue to-neon-purple',
      description: 'A bird\'s-eye view of all boards, people, and progress.',
      createdAt: now(),
      updatedAt: now(),
      widgets: [
        mkW({ type: 'number',         title: 'Total items',      settings: { metric: 'total_items' as MetricKind, color: '#A855F7' }, x: 0, y: 0, w: 3, h: 1 }),
        mkW({ type: 'number',         title: 'Done',             settings: { metric: 'done' as MetricKind,        color: '#00C875' }, x: 3, y: 0, w: 3, h: 1 }),
        mkW({ type: 'number',         title: 'Working on it',    settings: { metric: 'working' as MetricKind,     color: '#FDAB3D' }, x: 6, y: 0, w: 3, h: 1 }),
        mkW({ type: 'number',         title: 'Overdue',          settings: { metric: 'overdue' as MetricKind,     color: '#E2445C' }, x: 9, y: 0, w: 3, h: 1 }),
        mkW({ type: 'status_chart',   title: 'Items by status',    x: 0, y: 1, w: 6, h: 3 }),
        mkW({ type: 'priority_chart', title: 'Items by priority',  x: 6, y: 1, w: 6, h: 3 }),
        mkW({ type: 'workload',       title: 'Workload by person', x: 0, y: 4, w: 12, h: 3 }),
        mkW({ type: 'battery',        title: 'Progress',           x: 0, y: 7, w: 12, h: 1 }),
      ],
    },
  ];
}

interface DashboardState {
  dashboards: Dashboard[];
  createDashboard: (name: string, icon: string, color: string) => string;
  renameDashboard: (id: string, name: string) => void;
  deleteDashboard: (id: string) => void;
  addWidget: (dashboardId: string, widget: Omit<Widget, 'id'>) => string;
  updateWidget: (dashboardId: string, widgetId: string, patch: Partial<Widget>) => void;
  removeWidget: (dashboardId: string, widgetId: string) => void;
  resetToSeed: () => void;
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      dashboards: makeSeed(),
      createDashboard: (name, icon, color) => {
        const id = uid('dash');
        set((s) => ({
          dashboards: [...s.dashboards, {
            id, name, icon, color,
            widgets: [],
            createdAt: now(), updatedAt: now(),
          }],
        }));
        return id;
      },
      renameDashboard: (id, name) => set((s) => ({
        dashboards: s.dashboards.map((d) => d.id === id ? { ...d, name, updatedAt: now() } : d),
      })),
      deleteDashboard: (id) => set((s) => ({
        dashboards: s.dashboards.filter((d) => d.id !== id),
      })),
      addWidget: (dashboardId, widget) => {
        const id = uid('wg');
        set((s) => ({
          dashboards: s.dashboards.map((d) => d.id === dashboardId
            ? { ...d, widgets: [...d.widgets, { ...widget, id }], updatedAt: now() }
            : d,
          ),
        }));
        return id;
      },
      updateWidget: (dashboardId, widgetId, patch) => set((s) => ({
        dashboards: s.dashboards.map((d) => d.id === dashboardId
          ? { ...d, widgets: d.widgets.map((w) => w.id === widgetId ? { ...w, ...patch } : w), updatedAt: now() }
          : d,
        ),
      })),
      removeWidget: (dashboardId, widgetId) => set((s) => ({
        dashboards: s.dashboards.map((d) => d.id === dashboardId
          ? { ...d, widgets: d.widgets.filter((w) => w.id !== widgetId), updatedAt: now() }
          : d,
        ),
      })),
      resetToSeed: () => set({ dashboards: makeSeed() }),
    }),
    { name: 'myday-dashboards', version: 1 },
  ),
);
